import { TrashIcon } from '@phosphor-icons/react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { AddToProjectSubmenu } from '@/components/plans/AddToProjectSubmenu';
import { CardActionItem, CardActionMenu } from '@/components/ui/card-action-menu';
import { Confirm } from '@/components/ui/confirm';
import { EmptyState } from '@/components/ui/empty';
import { useInspector } from '@/components/ui/inspector';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/hooks/use-auth';
import { useBrandKits } from '@/hooks/useBrandKits';
import { logger } from '@/utils/logger';

// Lazy-load PostService (+ its BaseService dep) so they stay OUT of the
// first-paint entry chunk. The sidebar mounts on every authenticated route, so
// a static import dragged PostService/BaseService into the boot graph. These
// draft queries run just after mount (a background badge), not on the critical
// path — loading the service on demand keeps first paint lean.
const loadPostService = () => import('@/services/data/PostService').then((m) => m.postService);

const PAGE_SIZE = 20;
const COLLAPSED_COUNT = 3;

// Mirrors app-sidebar.tsx's Recent-chats row treatment exactly — sibling-surface
// parity, so a Draft row and a Recent-chat row are visually indistinguishable
// apart from their action menu. No unprefixed height/text-size — mobile
// inherits the sidebar primitive's own 44px row / 16px text defaults (see
// RECENT_TITLE_ROW in app-sidebar.tsx for why that matters).
// Same quieting as RECENT_TITLE_ROW (app-sidebar.tsx) — /75 → /55. A draft
// title is recall material, not a destination.
// The `dark:` half is load-bearing — see RECENT_TITLE_ROW in app-sidebar.tsx.
// Without it the primitive's `dark:text-sidebar-foreground/85` wins and this row
// renders at the nav colour, making the quieting a no-op in dark mode.
const DRAFT_TITLE_ROW =
  'md:h-7 text-foreground/55 dark:text-sidebar-foreground/55 hover:text-foreground dark:hover:text-sidebar-foreground pr-8';
const DRAFT_ACTION_TRIGGER =
  'absolute right-0 top-1/2 -translate-y-1/2 group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 md:opacity-0';

export const DraftLibrary = memo(function DraftLibrary() {
  const { user } = useAuth();
  const { activeKit, isActiveKitPending } = useBrandKits();
  const kitId = activeKit?.id ?? null;
  const queryClient = useQueryClient();
  const inspector = useInspector();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const { data, isPending, isError, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['drafts-infinite', user?.id, kitId],
      initialPageParam: 0,
      queryFn: async ({ pageParam }) => {
        const postService = await loadPostService();
        const res = await postService.getUserPostsPaginated(pageParam, PAGE_SIZE, {
          status: 'draft',
        });
        if (res.error) throw new Error(res.error.message);
        return res.data ?? { posts: [], nextPage: null };
      },
      getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
      enabled: !!user?.id && !isActiveKitPending,
      staleTime: 30_000,
    });

  const drafts = useMemo(() => data?.pages.flatMap((page) => page.posts) ?? [], [data]);

  // Scroll-triggered pagination — same IntersectionObserver-on-a-sentinel
  // convention as LibraryPage and the Recent-chats rail above it.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchNextPage();
      },
      { rootMargin: '160px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
    // `expanded` isn't read in the body, but the sentinel <div> only mounts
    // once expanded — re-run so the observer attaches to the freshly-mounted node.
  }, [hasNextPage, fetchNextPage, expanded]);

  const refreshDrafts = useCallback((): void => {
    queryClient.invalidateQueries({ queryKey: ['drafts-infinite', user?.id] });
  }, [queryClient, user?.id]);

  const deleteMutation = useMutation({
    mutationFn: async (postId: string) => {
      const postService = await loadPostService();
      const res = await postService.deletePost(postId);
      if (res.error) throw new Error(res.error.message);
    },
    onSuccess: () => {
      refreshDrafts();
      queryClient.invalidateQueries({ queryKey: ['posts'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['calendar-tab-posts'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['calendar-tab-count'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['draft-posts-count'], exact: false });
      setDeletingId(null);
    },
    onError: (error) => {
      logger.error('[DraftLibrary] delete failed', error);
      toast.error('Could not delete the draft.');
    },
  });

  const assignToProjectMutation = useMutation({
    mutationFn: async ({ postId, planId }: { postId: string; planId: string }) => {
      const postService = await loadPostService();
      const res = await postService.assignToCampaign(postId, planId);
      if (res.error) throw new Error(res.error.message);
    },
    onSuccess: () => {
      refreshDrafts();
      queryClient.invalidateQueries({ queryKey: ['campaigns', user?.id] });
      toast.success('Added to project');
    },
    onError: (error) => {
      logger.error('[DraftLibrary] assign to project failed', error);
      toast.error('Could not add to project.');
    },
  });

  const handleOpen = useCallback(
    (draftId: string) => {
      inspector.openSubject({ kind: 'post', postId: draftId }, { presentation: 'workspace' });
    },
    [inspector],
  );

  const handleDelete = useCallback((postId: string) => {
    setDeletingId(postId);
  }, []);

  const confirmDelete = useCallback(async (): Promise<void> => {
    if (!deletingId) return;
    await deleteMutation.mutateAsync(deletingId);
  }, [deletingId, deleteMutation]);

  if (isPending) {
    // Mirrors the resolved rows exactly: `gap-0.5` matches the real
    // `<SidebarMenu>` below (see the comment on that element), and each row's
    // height matches `DRAFT_TITLE_ROW` — `h-sidebar-row-mobile` on mobile
    // (SidebarMenuButton's default) overridden to `md:h-7` on desktop. The
    // previous version hardcoded `h-7` unconditionally with `space-y-1.5`,
    // ~40px too short per row on mobile.
    return (
      <SidebarMenu className="gap-0.5" aria-busy="true">
        {[1, 2, 3].map((i) => (
          <SidebarMenuItem key={i}>
            <Skeleton className="h-sidebar-row-mobile w-full rounded-md md:h-7" />
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    );
  }

  if (isError && drafts.length === 0) {
    return (
      <div className="flex flex-col items-center gap-1 py-4">
        <EmptyState variant="inline" title="Couldn't load drafts" className="py-0" />
        <button
          type="button"
          onClick={() => void refetch()}
          className="rounded-md px-2 py-1 text-detail text-muted-foreground transition-colors hover:text-foreground"
        >
          Try again
        </button>
      </div>
    );
  }

  if (drafts.length === 0) {
    return <EmptyState variant="inline" title="No drafts yet" />;
  }

  const visibleDrafts = expanded ? drafts : drafts.slice(0, COLLAPSED_COUNT);
  const hasMore = !expanded && (drafts.length > COLLAPSED_COUNT || hasNextPage);

  return (
    <>
      {/* `gap-0.5` matches `SIDEBAR_NAV_MENU` in `app-sidebar.tsx`, deliberately
          duplicated as a literal rather than imported — this component is a
          CHILD of that file, so importing from it would close a cycle. A bare
          `<SidebarMenu>` inherits the primitive's `gap-1` (4px), which put
          drafts at double the spacing of the nav rows above them. */}
      <SidebarMenu className="gap-0.5">
        {visibleDrafts.map((draft) => {
          const title = draft.content?.trim().slice(0, 60) || 'Untitled draft';
          return (
            <SidebarMenuItem key={draft.id}>
              <SidebarMenuButton
                tooltip={title}
                onClick={() => handleOpen(draft.id)}
                className={DRAFT_TITLE_ROW}
              >
                <span className="truncate">{title}</span>
              </SidebarMenuButton>
              <CardActionMenu
                label={`More actions for ${title}`}
                triggerClassName={DRAFT_ACTION_TRIGGER}
              >
                <AddToProjectSubmenu
                  currentProjectId={draft.campaign_id}
                  onSelectProject={(planId) =>
                    void assignToProjectMutation.mutateAsync({ postId: draft.id, planId })
                  }
                />
                <CardActionItem
                  icon={TrashIcon}
                  destructive
                  onSelect={() => handleDelete(draft.id)}
                >
                  Delete
                </CardActionItem>
              </CardActionMenu>
            </SidebarMenuItem>
          );
        })}
        {hasMore && (
          <SidebarMenuItem>
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="w-full rounded-md px-2 py-1.5 text-left text-detail text-muted-foreground transition-colors hover:text-foreground"
            >
              Show more
            </button>
          </SidebarMenuItem>
        )}
        {expanded &&
          isFetchingNextPage &&
          Array.from({ length: 3 }).map((_, i) => (
            <SidebarMenuItem key={`draft-skeleton-${i}`}>
              <div className="flex items-center p-2">
                <Skeleton className="h-3.5 w-[70%]" />
              </div>
            </SidebarMenuItem>
          ))}
      </SidebarMenu>
      {expanded && <div ref={sentinelRef} className="h-px" />}
      <Confirm
        open={deletingId !== null}
        onOpenChange={(o) => {
          if (!o) setDeletingId(null);
        }}
        variant="destructive"
        title="Delete draft?"
        description="This will permanently remove this draft. This action can't be undone."
        confirmLabel="Delete"
        onConfirm={confirmDelete}
      />
    </>
  );
});
